import { ExpressData } from '@features/specialistOffer/store/specialistSerivce';
import { Dictionary } from '@utils/util.types';
import { chatService } from 'services/chat';
import { getCropsByIds } from 'services/crops';
import { getLanguagesByIds, Language } from 'services/languages';
import { getServiceById, Service } from 'services/services';
import { Crop, getUserById, User } from 'services/users';

export function mapToDictionary<T extends Dictionary>(array: T[]): Dictionary[] {
	return array.map((item) => ({ id: item.id, name: item.name }));
}

export interface EnhancedExpressData extends ExpressData {
	farmer: User | null;
	crops: Crop | null;
	services: Service | null;
	languages: Language[];
	channelId: string;
	offer_id?: string;
}

export function getOtherUserId(expressRequest: ExpressData, currentUserId?: string): string {
	const { farmer_id, specialist_id } = expressRequest;
	if (!currentUserId) return specialist_id;
	return currentUserId === farmer_id ? specialist_id : farmer_id;
}

export async function mapExpressRequestToExpressData(
	expressRequest: ExpressData
): Promise<EnhancedExpressData> {
	if (!expressRequest) {
		throw new Error('Express request data is required');
	}

	const {
		farmer_id,
		crop_id,
		services_id,
		communication_language = [],
		specialist_id,
		request_id,
	} = expressRequest;

	try {
		const [languages, crops, services, farmer] = await Promise.all([
			communication_language.length > 0 ? getLanguagesByIds(communication_language) : [],
			crop_id ? getCropsByIds([crop_id]) : [],
			services_id ? getServiceById([services_id]) : [],
			farmer_id ? getUserById([farmer_id]) : [],
		]);

		return {
			...expressRequest,
			farmer: Array.isArray(farmer) ? farmer[0] || null : farmer,
			crops: Array.isArray(crops) ? crops[0] || null : crops,
			services: Array.isArray(services) ? services[0] || null : services,
			languages: languages || [],
			channelId: await chatService.getChannelIdByRequestAndUserId(request_id, specialist_id),
		};
	} catch (error) {
		console.error('Error mapping express request data:', error);

		return {
			...expressRequest,
			farmer: null,
			crops: null,
			services: null,
			languages: [],
			channelId: '',
		};
	}
}

export async function mapExpressRequestToExpressDataWithCurrentUser(
	expressRequest: ExpressData,
	currentUserId: string
): Promise<EnhancedExpressData> {
	if (!expressRequest) {
		throw new Error('Express request data is required');
	}

	const {
		farmer_id,
		crop_id,
		services_id,
		communication_language = [],
		request_id,
	} = expressRequest;

	try {
		const [languages, crops, services, farmer] = await Promise.all([
			communication_language.length > 0 ? getLanguagesByIds(communication_language) : [],
			crop_id ? getCropsByIds([crop_id]) : [],
			services_id ? getServiceById([services_id]) : [],
			farmer_id ? getUserById([farmer_id]) : [],
		]);
		console.log('currentUserId', currentUserId);
		const otherUserId = getOtherUserId(expressRequest, currentUserId);

		return {
			...expressRequest,
			farmer: Array.isArray(farmer) ? farmer[0] || null : farmer,
			crops: Array.isArray(crops) ? crops[0] || null : crops,
			services: Array.isArray(services) ? services[0] || null : services,
			languages: languages || [],
			channelId: await chatService.getChannelIdByRequestAndUserId(request_id, otherUserId),
		};
	} catch (error) {
		console.error('Error mapping express request data:', error);

		return {
			...expressRequest,
			farmer: null,
			crops: null,
			services: null,
			languages: [],
			channelId: '',
		};
	}
}

/**
 * Safely encodes a string to base64 format, handling Unicode characters properly.
 * This function can handle all Unicode characters including Georgian, Arabic, Chinese, etc.
 * 
 * @param str - The string to encode
 * @returns Base64 encoded string
 */
export function safeBase64Encode(str: string): string {
	return btoa(
		Array.from(new TextEncoder().encode(str))
			.map(byte => String.fromCharCode(byte))
			.join('')
	);
}
